import { useToast } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import FormField from '../../../components/fields/FormField';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { CreditPackDto } from '../hooks/useListCreditPacks';
import {
  UpdateCreditPackInput,
  useUpdateCreditPack,
} from '../hooks/useUpdateCreditPack';
import { validateCreditPackForm } from '../utils/credit-pack.utils';

interface EditCreditPackModalProps {
  pack: CreditPackDto;
  isOpen: boolean;
  onClose: () => void;
}

export default function EditCreditPackModal({
  pack,
  isOpen,
  onClose,
}: EditCreditPackModalProps) {
  const { mutateAsync: updatePack, isLoading } = useUpdateCreditPack();
  const [formData, setFormData] = useState<UpdateCreditPackInput>({});
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const toast = useToast();

  useEffect(() => {
    if (isOpen && pack) {
      setFormData({
        name: pack.name,
        priceInCents: pack.priceInCents,
        credits: pack.credits,
        sortOrder: pack.sortOrder,
      });
      setErrors({});
    }
  }, [isOpen, pack]);

  const handleInputChange = (field: keyof UpdateCreditPackInput, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors = validateCreditPackForm(
      {
        name: formData.name || '',
        priceInCents: formData.priceInCents || 0,
        credits: formData.credits || 0,
      },
      {
        nameRequired: translate('credit_pack.name_required'),
        priceRequired: translate('credit_pack.price_required'),
        creditsRequired: translate('credit_pack.credits_required'),
      },
    );
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      await updatePack({ id: pack.id, ...formData });
      toast({
        title: translate('credit_pack.updated_successfully'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (error: any) {
      toast({
        title: translate('credit_pack.update_error'),
        description: error.message || '',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={translate('credit_pack.edit_pack')}
      onSubmit={handleSubmit}
      submitLabel={translate('credit_pack.save')}
      cancelLabel={translate('common.cancel')}
      isSubmitting={isLoading}
      size="lg"
    >
      <FormStack>
        <FormField
          type="text"
          label={translate('credit_pack.name')}
          value={formData.name || ''}
          onChange={(val) => handleInputChange('name', val)}
          error={errors.name}
        />

        <FormField
          type="number"
          label={translate('credit_pack.price')}
          value={(formData.priceInCents || 0) / 100 || 0}
          onChange={(val) => handleInputChange('priceInCents', Math.round((val || 0) * 100))}
          min={0}
          step={0.5}
          error={errors.priceInCents}
          numberInputProps={{ precision: 2 }}
        />

        <FormField
          type="number"
          label={translate('credit_pack.credits')}
          value={formData.credits || 0}
          onChange={(val) => handleInputChange('credits', val || 0)}
          min={0.01}
          step={0.5}
          error={errors.credits}
          numberInputProps={{ precision: 2 }}
        />

        <FormField
          type="number"
          label={translate('credit_pack.sort_order')}
          value={formData.sortOrder ?? 0}
          onChange={(val) => handleInputChange('sortOrder', val || 0)}
          min={0}
        />
      </FormStack>
    </FormModal>
  );
}
